import locale from "@bonliva-crm/locale";
import {
  BulkActionCommunicationSubType,
  BulkActionType,
  SelectableBulkActionType
} from "@bonliva-crm/shared";
import { IRowNode } from "ag-grid-community";
import { useMemo, useState } from "react";
import { ExternalSelectionStepProps } from "../components/selection-step";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionData } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { Steps } from "../utils/step";
import { useConsultantEmailAction } from "./use-consultant-email-action";
import { useConsultantReassignBaAction } from "./use-consultant-reassign-ba-action";
import { useConsultantSMSAction } from "./use-consultant-sms-action";
import { useConsultantAssignCallListAction } from "./use-consultant-assign-call-list-action";
import { useConsultantDeleteAction } from "./use-consultant-delete-action";
import { useConsultantAssignTagsAction } from "./use-consultant-assign-tags-action";
import { useConsultantOrderQualityDocumentsAction } from "./use-consultant-order-quality-documents-action";

type Props<T extends SelectionStepRowType> = {
  type: SelectableBulkActionType;
  subType?: BulkActionCommunicationSubType | null;
  stepProps: {
    [Steps.Selection]: ExternalSelectionStepProps<T>;
    [Steps.Action]: ExternalActionStepProps<T>;
  };
};

export const useBulkActionData = <T extends SelectionStepRowType>(props: Props<T>) => {
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.Selection);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);

  const { entitySingular, entityPlural } = useMemo(() => {
    if (!props.type) return { entitySingular: "", entityPlural: "" };
    switch (props.type) {
      case BulkActionType.ConsultantEmail:
      case BulkActionType.ConsultantSMS:
      case BulkActionType.ConsultantReassignBA:
      case BulkActionType.ConsultantAssignCallList:
      case BulkActionType.ConsultantAssignTags:
      case BulkActionType.ConsultantDelete:
      case BulkActionType.ConsultantOrderQualityDocuments:
        return { entitySingular: locale.Consultant, entityPlural: locale.Consultants };
      default:
        throw new Error(`Unknown type: ${props.type}`);
    }
  }, [props.type]);

  // Some rows can be present in the pool but not selectable (e.g. consultants who
  // opted out of behovsutskick are shown greyed-out instead of being silently
  // dropped). The "X av N" counter and "select all" affordance must therefore be
  // based on the selectable rows, not the raw pool size.
  const { isRowSelectable, rowData } = props.stepProps[Steps.Selection];
  const selectableCount = useMemo(
    () =>
      rowData.filter((row) => isRowSelectable({ data: row } as IRowNode<T>)).length,
    [rowData, isRowSelectable]
  );

  const allSelected =
    selectableCount > 0 && selectableCount === selectedRows.length;
  const selectionText = allSelected
    ? `${locale.All} ${locale.numberOfChosenObjects(
        selectedRows.length,
        entitySingular,
        entityPlural
      )}`
    : `${selectedRows.length} ${locale.by} ${locale.numberOfChosenObjects(
        selectableCount,
        entitySingular,
        entityPlural
      )}`;

  const totalRows = selectedRows.length;
  const valueParsers = props.stepProps[Steps.Action].valueParsers;

  const consultantEmail = useConsultantEmailAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantEmail,
    subType: props.subType
  });

  const consultantSms = useConsultantSMSAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantSMS,
    subType: props.subType
  });

  const reassignBa = useConsultantReassignBaAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantReassignBA
  });

  const assignTags = useConsultantAssignTagsAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantAssignTags
  });

  const assignConsultantCallList = useConsultantAssignCallListAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantAssignCallList
  });

  const deleteConsultants = useConsultantDeleteAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantDelete
  });

  const orderQualityDocuments = useConsultantOrderQualityDocumentsAction({
    entityPlural,
    entitySingular,
    selectedRows,
    totalRows,
    valueParser: valueParsers.ConsultantOrderQualityDocuments
  });

  const resetValues = () => {
    setCurrentStep(Steps.Selection);
    setSelectedRows([]);
    consultantEmail.reset();
    consultantSms.reset();
    reassignBa.reset();
    assignConsultantCallList.reset();
    deleteConsultants.reset();
    assignTags.reset();
    orderQualityDocuments.reset();
  };

  const actionMeta = useMemo(() => {
    if (!props.type) return { nextStepDisabled: true, nextStepDisabledToolTip: "" };

    switch (props.type) {
      case BulkActionType.ConsultantEmail:
        return {
          nextStepDisabled: consultantEmail.nextStepDisabled,
          nextStepDisabledToolTip: locale.subjectAndContentsRequired
        };
      case BulkActionType.ConsultantSMS:
        return {
          nextStepDisabled: consultantSms.nextStepDisabled,
          nextStepDisabledToolTip: locale.required(locale.Message)
        };
      case BulkActionType.ConsultantReassignBA:
        return {
          nextStepDisabled: reassignBa.nextStepDisabled,
          nextStepDisabledToolTip: locale.required(locale.selection)
        };
      case BulkActionType.ConsultantAssignCallList:
        return {
          nextStepDisabled: assignConsultantCallList.nextStepDisabled,
          nextStepDisabledToolTip: locale.required(locale.selection)
        };
      case BulkActionType.ConsultantAssignTags:
        return {
          nextStepDisabled: assignTags.nextStepDisabled,
          nextStepDisabledToolTip: locale.required(locale.selection)
        };
      case BulkActionType.ConsultantDelete:
        return {
          nextStepDisabled: false,
          nextStepDisabledToolTip: ""
        };
      case BulkActionType.ConsultantOrderQualityDocuments:
        return {
          nextStepDisabled: orderQualityDocuments.nextStepDisabled,
          nextStepDisabledToolTip: locale.required(locale.selection)
        };
      default:
        throw new Error(`Unknown next step disabled type: ${props.type}`);
    }
  }, [
    assignConsultantCallList.nextStepDisabled,
    assignTags.nextStepDisabled,
    consultantEmail.nextStepDisabled,
    consultantSms.nextStepDisabled,
    props.type,
    reassignBa.nextStepDisabled,
    orderQualityDocuments.nextStepDisabled
  ]);

  const validRows = useMemo(() => {
    if (!valueParsers[props.type]) return 0;
    return valueParsers[props.type](selectedRows).nodeIds.length;
  }, [props.type, selectedRows, valueParsers]);

  const stepData = {
    [Steps.Selection]: {
      previousStep: null,
      nextStep:
        props.type === BulkActionType.ConsultantDelete
          ? Steps.Confirmation
          : Steps.Action,
      nextStepDisabled: selectedRows.length === 0,
      nextStepDisabledToolTip: locale.selectionRequiredToContinue(
        entitySingular.toLowerCase(),
        "en"
      )
    },
    [Steps.Action]: {
      previousStep: Steps.Selection,
      nextStep: Steps.Confirmation,
      nextStepDisabled: actionMeta.nextStepDisabled,
      nextStepDisabledToolTip: actionMeta.nextStepDisabledToolTip
    },
    [Steps.Confirmation]: {
      previousStep:
        props.type === BulkActionType.ConsultantDelete ? Steps.Selection : Steps.Action,
      nextStep: null,
      nextStepDisabled: validRows === 0,
      nextStepDisabledToolTip: locale.noValidEntities(entityPlural.toLowerCase())
    }
  };

  const bulkActionData: Record<SelectableBulkActionType, BulkActionData> = {
    [BulkActionType.ConsultantEmail]: {
      ...consultantEmail,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    },
    [BulkActionType.ConsultantSMS]: {
      ...consultantSms,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    },
    [BulkActionType.ConsultantReassignBA]: {
      ...reassignBa,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    },
    [BulkActionType.ConsultantAssignCallList]: {
      ...assignConsultantCallList,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    },
    [BulkActionType.ConsultantAssignTags]: {
      ...assignTags,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    },
    [BulkActionType.ConsultantDelete]: {
      ...deleteConsultants,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    },
    [BulkActionType.ConsultantOrderQualityDocuments]: {
      ...orderQualityDocuments,
      entitySingular,
      entityPlural,
      selectionText,
      allSelected
    }
  };

  return {
    bulkActionData,
    currentStep,
    setCurrentStep,
    stepData,
    resetValues,
    selectedRows,
    setSelectedRows
  };
};
